import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './POSubmitted.css';
import API_BASE_URL from '../config';
import RedirectLinks from './RedirectLinks';
import LMSStatus from './LMSStatus';
import { useAuth } from '../context/AuthContext';

const STATUS_OPTIONS = ['', 'created', 'sent', 'accepted', 'rejected', 'paid', 'cancelled', 'error'];
const ONBOARDING_OPTIONS = ['', 'PENDING', 'APPROVED', 'REJECTED', 'ERROR'];
const L7_MATCH_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'mismatch', label: 'Not match' },
  { value: 'match', label: 'Match' },
  { value: 'failed', label: 'Check failed' },
  { value: 'unchecked', label: 'Not checked' }
];
const POLICY_TYPE_OPTIONS = [
  'MOTOR_INSURANCE',
  'COMPULSORY_MOTOR_INSURANCE',
  'MOTOR_ADD_ON',
  'MICROPLUS',
  'PET',
  'USE_CAR_WARRANTY'
];

function fmtMoney(v) {
  if (v == null || v === '') return '-';
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDateTime(s) {
  if (!s) return '-';
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleString('th-TH', { hour12: false });
  } catch {
    return s;
  }
}

// Resolve the APFS redirectURL for a submission. New submissions persist it
// into the dedicated `redirect_url` column; older rows only have it embedded
// in the raw `apfs_response` JSON. Try the column first, then fall back to
// probing the response body under both casings the API has been seen to use.
function getRedirectFromSubmission(sub) {
  if (!sub) return null;
  if (sub.redirect_url) return sub.redirect_url;
  const resp = sub.apfs_response;
  if (!resp || typeof resp !== 'object') return null;
  return (
    resp?.data?.redirectUrl ||
    resp?.data?.redirectURL ||
    resp?.redirectUrl ||
    resp?.redirectURL ||
    null
  );
}

function fmtMismatchValue(v) {
  return v == null || v === '' ? '(empty)' : String(v);
}

// "L7 Not match" cell: result of re-pulling the PO when L.7 was sent and
// comparing it with what L.2 submitted. Never blocks L.7 — report only.
function L7MatchBadge({ row }) {
  if (row.l7_po_check_error) {
    return (
      <span className="apfs-pay-badge apfs-pay-none" title={`PO re-check failed: ${row.l7_po_check_error}`}>
        Check failed
      </span>
    );
  }
  if (row.l7_po_mismatch_count == null) return <span style={{ color: '#888' }}>-</span>;
  if (row.l7_po_mismatch_count === 0) {
    return <span style={{ color: '#2e7d32', fontSize: 12, fontWeight: 600 }}>✓ Match</span>;
  }
  const title = (row.l7_po_mismatches || [])
    .map((m) => `${m.label}: ${fmtMismatchValue(m.submitted)} → ${fmtMismatchValue(m.current)}`)
    .join('\n');
  return (
    <span title={title} style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 12,
      background: '#c6282822', color: '#c62828', fontSize: 12, fontWeight: 600, cursor: 'help'
    }}>
      ⚠️ {row.l7_po_mismatch_count} field{row.l7_po_mismatch_count > 1 ? 's' : ''}
    </span>
  );
}

function PoMismatchTable({ mismatches }) {
  if (!mismatches || mismatches.length === 0) return null;
  return (
    <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%' }}>
      <thead>
        <tr style={{ background: '#fdecea' }}>
          <th style={{ textAlign: 'left', padding: '4px 8px' }}>Field</th>
          <th style={{ textAlign: 'left', padding: '4px 8px' }}>Submitted (L.2)</th>
          <th style={{ textAlign: 'left', padding: '4px 8px' }}>PO now</th>
        </tr>
      </thead>
      <tbody>
        {mismatches.map((m) => (
          <tr key={m.field} style={{ borderTop: '1px solid #f5c2c0' }}>
            <td style={{ padding: '4px 8px' }}>{m.label}</td>
            <td style={{ padding: '4px 8px' }}>{fmtMismatchValue(m.submitted)}</td>
            <td style={{ padding: '4px 8px', color: '#c62828', fontWeight: 600 }}>{fmtMismatchValue(m.current)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// Shown in the L.7 modal / tab right after a send: L.7 went through either
// way, this only tells the operator whether the PO has drifted since L.2.
function PoCheckNotice({ poCheck }) {
  if (!poCheck) return null;
  if (!poCheck.checked) {
    return (
      <div style={{ background: '#fff8e1', color: '#8d6e00', padding: 10, borderRadius: 4, marginTop: 8, fontSize: 13 }}>
        ⚠️ ดึง PO เพื่อเทียบกับ L.2 ไม่สำเร็จ: {poCheck.error}
      </div>
    );
  }
  if (poCheck.mismatches.length === 0) return null;
  return (
    <div style={{ background: '#fdecea', color: '#b3261e', padding: 10, borderRadius: 4, marginTop: 8, fontSize: 13 }}>
      ⚠️ ส่ง L.7 แล้ว แต่ PO ปัจจุบันไม่ตรงกับที่ส่ง L.2 จำนวน {poCheck.mismatches.length} ช่อง:
      <div style={{ marginTop: 6 }}><PoMismatchTable mismatches={poCheck.mismatches} /></div>
    </div>
  );
}

function APFSSubmitted() {
  const { user, can } = useAuth();
  const isAdmin = user?.role === 'admin';
  const canEdit = can('apfs-submitted', 'edit'); // Send/Update Policy (L.7)
  const canLms = can('lms-status', 'view');      // L.11 LMS status
  const [lmsPropId, setLmsPropId] = useState(null);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [rejectingId, setRejectingId] = useState(null);

  const [filters, setFilters] = useState({
    prop_id: '',
    po_number: '',
    customer: '',
    status: '',
    program_code: '',
    agent_id: '',
    paid: '',
    date_from: '',
    date_to: '',
    onboarding: '',
    l7_match: ''
  });

  // Dropdown option sources (loaded once).
  const [programOptions, setProgramOptions] = useState([]);
  const [agentOptions, setAgentOptions] = useState([]);

  // Onboarding (L.7) modal state
  const [onboarding, setOnboarding] = useState(null);
  // shape: { propId, crm, form, warnings, alreadySent, lastResult, loading, submitting, error }

  const [selected, setSelected] = useState(null); // { submission, callbacks, apiLogs }
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [detailTab, setDetailTab] = useState('overview'); // overview | payload | response | logs | callbacks | update-policy

  // Inline-expanded row showing the redirectURL copy links (one row at a time).
  const [expandedLinksId, setExpandedLinksId] = useState(null);

  const [tabPolicy, setTabPolicy] = useState(null);
  // shape: { loading, form, warnings, alreadySent, lastResult, lastSentAt, submitting, error, result, apfsErrors, apfsTitle }

  const fetchRows = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(pagination.limit)
      });
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });

      const resp = await axios.get(`${API_BASE_URL}/api/apfs/submissions?${params}`);
      if (resp.data.success) {
        setRows(resp.data.data || []);
        setPagination(resp.data.pagination);
      } else {
        setError(resp.data.error || 'Failed to load APFS submissions');
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [pagination.limit, filters]);

  useEffect(() => { fetchRows(1); }, [fetchRows]);

  // Load Program + Agent dropdown options once.
  useEffect(() => {
    (async () => {
      try {
        const [pRes, aRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/apfs/programs`),
          axios.get(`${API_BASE_URL}/api/apfs/eod/agents`),
        ]);
        if (pRes.data?.success) setProgramOptions(pRes.data.programs || []);
        if (aRes.data?.success) setAgentOptions(aRes.data.agents || []);
      } catch { /* non-fatal — filters still work as free text fallback */ }
    })();
  }, []);

  const openDetail = async (propId) => {
    setSelected({ loading: true });
    setDetailLoading(true);
    setDetailError(null);
    setDetailTab('overview');
    setTabPolicy(null);
    try {
      const resp = await axios.get(`${API_BASE_URL}/api/apfs/submissions/${encodeURIComponent(propId)}`);
      if (resp.data.success) {
        setSelected(resp.data);
      } else {
        setDetailError(resp.data.error || 'Failed to load detail');
      }
    } catch (e) {
      setDetailError(e.response?.data?.error || e.message);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => { setSelected(null); setDetailError(null); setTabPolicy(null); };

  const switchDetailTab = async (tab, propId) => {
    setDetailTab(tab);
    if (tab === 'update-policy' && propId && !tabPolicy) {
      setTabPolicy({ loading: true });
      try {
        const resp = await axios.get(`${API_BASE_URL}/api/apfs/submissions/${encodeURIComponent(propId)}/onboarding-prefill`);
        if (resp.data.success) {
          setTabPolicy({
            loading: false, submitting: false, error: null, result: null,
            crm: resp.data.crm,
            alreadySent: resp.data.alreadySent,
            lastResult: resp.data.lastResult,
            lastSentAt: resp.data.lastSentAt,
            warnings: resp.data.warnings || [],
            form: { ...resp.data.suggested }
          });
        } else {
          setTabPolicy({ loading: false, error: resp.data.error || 'Failed to load' });
        }
      } catch (e) {
        setTabPolicy({ loading: false, error: e.response?.data?.error || e.message });
      }
    }
  };

  const updateTabPolicyField = (key, value) =>
    setTabPolicy((p) => p ? { ...p, form: { ...p.form, [key]: value } } : p);

  const submitTabPolicy = async () => {
    if (!tabPolicy?.form || !selected?.submission) return;
    const { decision, insurerCode, policyType, policyNumber, policyUrl, policyStartDate, policyEndDate } = tabPolicy.form;
    if (!decision) return;
    if (!insurerCode) { setTabPolicy((p) => ({ ...p, error: 'insurerCode is required' })); return; }
    if (!policyNumber) {
      setTabPolicy((p) => ({
        ...p,
        error: decision === 'APPROVED' ? 'Policy Number is required when APPROVED' : 'Endorsement Number is required when REJECTED'
      }));
      return;
    }
    setTabPolicy((p) => ({ ...p, submitting: true, error: null, apfsErrors: null }));
    try {
      const resp = await axios.put(`${API_BASE_URL}/api/apfs/contracts/status`, {
        crm: tabPolicy.crm,
        decision, insurerCode, policyType, policyNumber, policyUrl, policyStartDate, policyEndDate
      });
      setTabPolicy((p) => ({ ...p, submitting: false, result: resp.data, error: null, apfsErrors: null, alreadySent: true, lastResult: decision }));
      fetchRows(pagination.page);
    } catch (e) {
      const body = e.response?.data || {};
      setTabPolicy((p) => ({ ...p, submitting: false, error: body.error || e.message, apfsErrors: body.apfsErrors || null, apfsTitle: body.apfsTitle || null }));
    }
  };

  // Admin-only: internal reject so the Prop ID can be re-submitted. Does NOT
  // contact APFS today (structured for future L.12 cancel wiring).
  const rejectSubmission = async (row) => {
    const reason = window.prompt(
      `Reject Prop ID ${row.prop_id}?\n\n` +
      `This marks it 'rejected' internally so it can be re-submitted on the Policy Submit page. ` +
      `APFS is NOT contacted.\n\nOptional reason:`,
      ''
    );
    if (reason === null) return; // cancelled
    setRejectingId(row.id);
    try {
      const resp = await axios.post(
        `${API_BASE_URL}/api/apfs/submissions/${encodeURIComponent(row.prop_id)}/reject`,
        { reason: reason || null }
      );
      if (resp.data?.success) {
        await fetchRows(pagination.page);
      } else {
        alert(resp.data?.error || 'Reject failed');
      }
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    } finally {
      setRejectingId(null);
    }
  };

  const onFilterChange = (key, value) => setFilters((f) => ({ ...f, [key]: value }));
  const clearFilters = () => setFilters({ prop_id: '', po_number: '', customer: '', status: '', program_code: '', agent_id: '', paid: '', date_from: '', date_to: '', onboarding: '', l7_match: '' });

  const openOnboarding = async (propId) => {
    setOnboarding({ propId, loading: true });
    try {
      const resp = await axios.get(`${API_BASE_URL}/api/apfs/submissions/${encodeURIComponent(propId)}/onboarding-prefill`);
      if (resp.data.success) {
        setOnboarding({
          propId,
          crm: resp.data.crm,
          alreadySent: resp.data.alreadySent,
          lastResult: resp.data.lastResult,
          lastSentAt: resp.data.lastSentAt,
          warnings: resp.data.warnings || [],
          form: { ...resp.data.suggested },
          loading: false,
          submitting: false,
          error: null,
          result: null
        });
      } else {
        setOnboarding({ propId, loading: false, error: resp.data.error || 'Failed to load' });
      }
    } catch (e) {
      setOnboarding({ propId, loading: false, error: e.response?.data?.error || e.message });
    }
  };

  const closeOnboarding = () => setOnboarding(null);

  const updateOnboardingField = (key, value) =>
    setOnboarding((o) => o ? { ...o, form: { ...o.form, [key]: value } } : o);

  const submitOnboarding = async () => {
    if (!onboarding || !onboarding.form) return;
    const { decision, insurerCode, policyType, policyNumber, policyUrl, policyStartDate, policyEndDate } = onboarding.form;
    if (!decision) return;
    if (!insurerCode) {
      setOnboarding((o) => ({ ...o, error: 'insurerCode is required' })); return;
    }
    if (!policyNumber) {
      setOnboarding((o) => ({
        ...o,
        error: decision === 'APPROVED'
          ? 'Policy Number is required when APPROVED'
          : 'Endorsement Number is required when REJECTED'
      })); return;
    }

    setOnboarding((o) => ({ ...o, submitting: true, error: null, apfsErrors: null }));
    try {
      const resp = await axios.put(`${API_BASE_URL}/api/apfs/contracts/status`, {
        crm: onboarding.crm,
        decision,
        insurerCode,
        policyType,
        policyNumber,
        policyUrl,
        policyStartDate,
        policyEndDate
      });
      // axios with default validateStatus throws on 4xx, so this branch is success.
      setOnboarding((o) => ({ ...o, submitting: false, result: resp.data, error: null, apfsErrors: null }));
      fetchRows(pagination.page);
    } catch (e) {
      const body = e.response?.data || {};
      setOnboarding((o) => ({
        ...o,
        submitting: false,
        error: body.error || e.message,
        apfsErrors: body.apfsErrors || null,
        apfsTitle: body.apfsTitle || null,
        result: body
      }));
    }
  };

  const renderStatusBadge = (s) => {
    const color = ({
      sent: '#1976d2',
      accepted: '#2e7d32',
      paid: '#2e7d32',
      rejected: '#c62828',
      error: '#c62828',
      cancelled: '#757575',
      created: '#616161'
    })[s] || '#616161';
    return (
      <span style={{
        display: 'inline-block', padding: '2px 8px', borderRadius: 12,
        background: `${color}22`, color, fontSize: 12, fontWeight: 600
      }}>{s || '-'}</span>
    );
  };

  return (
    <div className="po-submitted">
      <h2>APFS Submitted</h2>

      <div className="filters-section">
        <div className="filter-row">
          <div className="filter-group">
            <label>Prop ID / PO</label>
            <input value={filters.prop_id} onChange={(e) => onFilterChange('prop_id', e.target.value)} placeholder="PO68..." />
          </div>
          <div className="filter-group">
            <label>Customer</label>
            <input value={filters.customer} onChange={(e) => onFilterChange('customer', e.target.value)} placeholder="ชื่อ / นามสกุล" />
          </div>
          <div className="filter-group">
            <label>Program</label>
            <select value={filters.program_code} onChange={(e) => onFilterChange('program_code', e.target.value)}>
              <option value="">All</option>
              {programOptions.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div className="filter-group">
            <label>Agent</label>
            <select value={filters.agent_id} onChange={(e) => onFilterChange('agent_id', e.target.value)}>
              <option value="">All</option>
              {agentOptions.map((a) => (
                <option key={a.agent_id} value={a.agent_id}>
                  {a.agent_id}{a.agent_name && a.agent_name !== a.agent_id ? ` — ${a.agent_name}` : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>APFS Payment</label>
            <select value={filters.paid} onChange={(e) => onFilterChange('paid', e.target.value)}>
              <option value="">All</option>
              <option value="yes">Paid</option>
              <option value="no">Unpaid</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Status</label>
            <select value={filters.status} onChange={(e) => onFilterChange('status', e.target.value)}>
              {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s || 'All'}</option>)}
            </select>
          </div>
          <div className="filter-group">
            <label>Onboarding (L.7)</label>
            <select value={filters.onboarding} onChange={(e) => onFilterChange('onboarding', e.target.value)}>
              {ONBOARDING_OPTIONS.map((s) => <option key={s} value={s}>{s || 'All'}</option>)}
            </select>
          </div>
          <div className="filter-group">
            <label>L7 Not match</label>
            <select value={filters.l7_match} onChange={(e) => onFilterChange('l7_match', e.target.value)}>
              {L7_MATCH_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="filter-group">
            <label>Date from</label>
            <input type="date" value={filters.date_from} onChange={(e) => onFilterChange('date_from', e.target.value)} />
          </div>
          <div className="filter-group">
            <label>Date to</label>
            <input type="date" value={filters.date_to} onChange={(e) => onFilterChange('date_to', e.target.value)} />
          </div>
          <div className="filter-actions">
            <button className="btn-search" onClick={() => fetchRows(1)}>Search</button>
            <button className="btn-clear" onClick={clearFilters}>Clear</button>
          </div>
        </div>
      </div>

      {error && <div style={{ color: '#c62828', padding: 12 }}>⚠️ {error}</div>}

      <div style={{ marginBottom: 8, color: '#666' }}>
        {loading
          ? 'Loading...'
          : `Showing ${rows.length} of ${pagination.total} submissions (Page ${pagination.page}/${pagination.totalPages || 1})`}
      </div>

      <div className="submissions-table-wrapper">
        <table className="submissions-table">
          <thead>
            <tr>
              <th>Prop ID</th>
              <th>Customer</th>
              <th>Program</th>
              <th style={{ textAlign: 'right' }}>Gross Premium</th>
              <th style={{ textAlign: 'right' }}>After Discount</th>
              <th>Pay Method</th>
              <th>APFS Payment</th>
              <th>Status</th>
              <th>Onboarding</th>
              <th>L7 Not match</th>
              <th>Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={12} style={{ textAlign: 'center', padding: 24, color: '#888' }}>
                {loading ? 'Loading...' : 'No APFS submissions found'}
              </td></tr>
            ) : rows.flatMap((r) => {
              const isExpanded = expandedLinksId === r.id;
              const redirectUrl = getRedirectFromSubmission(r);
              const hasRedirect = !!redirectUrl;
              return [
                <tr key={r.id}>
                  <td><code>{r.prop_id}</code></td>
                  <td>{[r.p_holder_first_name, r.p_holder_last_name].filter(Boolean).join(' ') || '-'}<br />
                      <small style={{ color: '#888' }}>{r.p_holder_phone || ''}</small></td>
                  <td><small>{r.program_code || '-'}</small></td>
                  <td style={{ textAlign: 'right' }}>{fmtMoney(r.trans_gross_premium)}</td>
                  <td style={{ textAlign: 'right' }}>{fmtMoney(r.trans_gross_premium_after_discount)}</td>
                  <td>{r.preferred_payment_method || '-'}</td>
                  <td>
                    {r.eod_paid
                      ? <span className="apfs-pay-badge apfs-pay-paid" title={r.eod_agent_id ? `Agent ${r.eod_agent_id}${r.eod_confirm_date ? ' • ' + fmtDateTime(r.eod_confirm_date) : ''}` : 'Matched in EOD'}>Paid</span>
                      : <span className="apfs-pay-badge apfs-pay-none">-</span>}
                  </td>
                  <td>{renderStatusBadge(r.status)}</td>
                  <td>{r.onboarding_status || '-'}</td>
                  <td><L7MatchBadge row={r} /></td>
                  <td><small>{fmtDateTime(r.created_at)}</small></td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                    <button className="btn-search" style={{ padding: '4px 10px', fontSize: 12 }}
                            onClick={() => openDetail(r.prop_id)}>View</button>
                    {isAdmin && (
                      <button
                        className="btn-search"
                        title="Admin: internally reject so this Prop ID can be re-submitted (APFS not contacted)"
                        disabled={rejectingId === r.id || String(r.status).toLowerCase() === 'rejected'}
                        style={{
                          padding: '4px 10px', fontSize: 12,
                          background: '#fdecea', color: '#b3261e', borderColor: '#f5c2c0',
                          opacity: String(r.status).toLowerCase() === 'rejected' ? 0.5 : 1
                        }}
                        onClick={() => rejectSubmission(r)}
                      >
                        {rejectingId === r.id ? '…' : (String(r.status).toLowerCase() === 'rejected' ? 'Rejected' : 'Reject')}
                      </button>
                    )}
                    <button
                      className="btn-search"
                      title={hasRedirect ? 'Show copy links' : 'No redirect URL yet'}
                      disabled={!hasRedirect}
                      style={{
                        padding: '4px 10px',
                        fontSize: 12,
                        marginRight: 6,
                        background: isExpanded ? '#4f46e5' : '#eef2ff',
                        color: isExpanded ? '#fff' : '#3730a3',
                        borderColor: '#c7d2fe',
                        opacity: hasRedirect ? 1 : 0.5,
                        cursor: hasRedirect ? 'pointer' : 'not-allowed'
                      }}
                      onClick={() => setExpandedLinksId(isExpanded ? null : r.id)}
                    >
                      🔗 {isExpanded ? 'Hide' : 'Links'}
                    </button>
                    {canEdit && (
                      <button
                        className="btn-search"
                        style={{
                          padding: '4px 10px',
                          fontSize: 12,
                          background: r.onboarding_status ? '#ef6c00' : '#2e7d32',
                          borderColor: r.onboarding_status ? '#ef6c00' : '#2e7d32'
                        }}
                        onClick={() => openOnboarding(r.prop_id)}
                      >
                        {r.onboarding_status ? 'Update Policy' : 'Send Policy'}
                      </button>
                    )}
                    {canLms && (
                      <button
                        className="btn-search"
                        title={r.lms_synced_at
                          ? `L.11 — ดึงล่าสุด ${fmtDateTime(r.lms_synced_at)} (เปิดดูข้อมูลที่บันทึกไว้)`
                          : 'L.11 — ยังไม่เคยดึง (จะดึงจาก APFS)'}
                        style={{ padding: '4px 10px', fontSize: 12, background: '#e0f2f1', color: '#00695c', borderColor: '#80cbc4' }}
                        onClick={() => setLmsPropId(r.prop_id)}
                      >
                        L.11{r.lms_synced_at ? ' ✓' : ''}
                      </button>
                    )}
                    </div>
                  </td>
                </tr>,
                isExpanded && hasRedirect && (
                  <tr key={`${r.id}-links`}>
                    <td colSpan={12} style={{ background: '#f8faff', padding: '10px 14px', borderTop: 'none' }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#3730a3', marginBottom: 4 }}>
                        🔗 ลิงก์ชำระเงิน (redirectURL) — {r.prop_id}
                      </div>
                      <RedirectLinks url={redirectUrl} />
                    </td>
                  </tr>
                )
              ].filter(Boolean);
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end', marginTop: 12 }}>
          <button disabled={pagination.page <= 1} onClick={() => fetchRows(pagination.page - 1)}>Prev</button>
          <span>{pagination.page} / {pagination.totalPages}</span>
          <button disabled={pagination.page >= pagination.totalPages} onClick={() => fetchRows(pagination.page + 1)}>Next</button>
        </div>
      )}

      {/* Detail modal */}
      {selected && (
        <div onClick={closeDetail}
             style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex',
                      alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div onClick={(e) => e.stopPropagation()}
               style={{ background: '#fff', width: 'min(960px, 95vw)', maxHeight: '90vh', overflow: 'auto',
                        borderRadius: 8, padding: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0 }}>APFS Submission — {selected.submission?.prop_id || '...'}</h3>
              <button onClick={closeDetail}>✕</button>
            </div>

            {detailLoading && <div style={{ padding: 20 }}>Loading…</div>}
            {detailError && <div style={{ color: '#c62828', padding: 12 }}>⚠️ {detailError}</div>}

            {selected.submission && (
              <>
                <div style={{ display: 'flex', gap: 8, margin: '16px 0', borderBottom: '1px solid #eee' }}>
                  {['overview', 'payload', 'response', 'callbacks', 'logs', 'update policy'].map((t) => (
                    <button key={t}
                            onClick={() => switchDetailTab(t === 'update policy' ? 'update-policy' : t, selected.submission?.prop_id)}
                            style={{
                              padding: '8px 14px', border: 'none',
                              background: detailTab === (t === 'update policy' ? 'update-policy' : t) ? '#1976d2' : 'transparent',
                              color: detailTab === (t === 'update policy' ? 'update-policy' : t) ? '#fff' : '#333',
                              borderRadius: '6px 6px 0 0', cursor: 'pointer'
                            }}>{t}</button>
                  ))}
                </div>

                {detailTab === 'overview' && (
                  <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 8 }}>
                    <div><b>Prop ID</b></div><div><code>{selected.submission.prop_id}</code></div>
                    <div><b>Status</b></div><div>{renderStatusBadge(selected.submission.status)}</div>
                    <div><b>Program</b></div><div>{selected.submission.program_code || '-'}</div>
                    <div><b>Pay Method</b></div><div>{selected.submission.preferred_payment_method || '-'}</div>
                    <div><b>Customer</b></div><div>
                      {[selected.submission.p_holder_first_name, selected.submission.p_holder_last_name].filter(Boolean).join(' ') || '-'}<br />
                      {selected.submission.p_holder_phone || ''} {selected.submission.p_holder_email ? `• ${selected.submission.p_holder_email}` : ''}
                    </div>
                    <div><b>Gross Premium</b></div><div>{fmtMoney(selected.submission.trans_gross_premium)}</div>
                    <div><b>Discount</b></div><div>{fmtMoney(selected.submission.trans_gross_premium_discount)}</div>
                    <div><b>After Discount</b></div><div>{fmtMoney(selected.submission.trans_gross_premium_after_discount)}</div>
                    <div><b>Net Premium</b></div><div>{fmtMoney(selected.submission.trans_net_premium)}</div>
                    <div><b>VAT</b></div><div>{fmtMoney(selected.submission.vat)}</div>
                    <div><b>Stamp Duty</b></div><div>{fmtMoney(selected.submission.trans_stamp_duty)}</div>
                    <div><b>Coverage</b></div><div>{selected.submission.start_date || '-'} → {selected.submission.end_date || '-'}</div>
                    <div><b>Car</b></div><div>{[selected.submission.make_c, selected.submission.model, selected.submission.year_of_manufacture].filter(Boolean).join(' ') || '-'}</div>
                    <div><b>Redirect URL</b></div><div>
                      {(() => {
                        const url = getRedirectFromSubmission(selected.submission);
                        return url ? <RedirectLinks url={url} /> : '-';
                      })()}
                    </div>
                    <div><b>Onboarding</b></div><div>{selected.submission.onboarding_status || '-'}</div>
                    <div><b>Policy No</b></div><div>{selected.submission.policy_number || '-'}</div>
                    <div><b>L7 PO check</b></div><div>
                      <L7MatchBadge row={selected.submission} />
                      {selected.submission.l7_po_checked_at && (
                        <small style={{ color: '#888', marginLeft: 8 }}>{fmtDateTime(selected.submission.l7_po_checked_at)}</small>
                      )}
                      {selected.submission.l7_po_mismatch_count > 0 && (
                        <div style={{ marginTop: 6 }}><PoMismatchTable mismatches={selected.submission.l7_po_mismatches} /></div>
                      )}
                    </div>
                    <div><b>Created</b></div><div>{fmtDateTime(selected.submission.created_at)}</div>
                  </div>
                )}

                {detailTab === 'payload' && (
                  <pre style={{ background: '#f5f5f5', padding: 12, borderRadius: 4, overflow: 'auto', maxHeight: '60vh' }}>
                    {JSON.stringify(selected.submission.crm_payload, null, 2)}
                  </pre>
                )}

                {detailTab === 'response' && (
                  <pre style={{ background: '#f5f5f5', padding: 12, borderRadius: 4, overflow: 'auto', maxHeight: '60vh' }}>
                    {JSON.stringify(selected.submission.apfs_response, null, 2)}
                  </pre>
                )}

                {detailTab === 'callbacks' && (
                  <div>
                    {(selected.callbacks || []).length === 0
                      ? <div style={{ color: '#888' }}>No callbacks received.</div>
                      : selected.callbacks.map((cb) => (
                        <div key={cb.id} style={{ border: '1px solid #eee', padding: 8, borderRadius: 4, marginBottom: 8 }}>
                          <div><b>{cb.callback_type}</b> · {fmtDateTime(cb.received_at)}
                               {cb.term ? ` · term ${cb.term}` : ''}
                               {cb.paid_status ? ` · ${cb.paid_status}` : ''}
                               {cb.term_of_payment ? ` · ${fmtMoney(cb.term_of_payment)}` : ''}</div>
                          <pre style={{ background: '#fafafa', padding: 8, marginTop: 6, fontSize: 12 }}>
                            {JSON.stringify(cb.payload, null, 2)}
                          </pre>
                        </div>
                      ))}
                  </div>
                )}

                {detailTab === 'logs' && (
                  <div>
                    {(selected.apiLogs || []).length === 0 && (
                      <div style={{ color: '#888' }}>No API logs found.</div>
                    )}
                    {(selected.apiLogs || []).map((l) => (
                      <div key={l.id} style={{ border: '1px solid #e0e0e0', borderRadius: 6, marginBottom: 12, overflow: 'hidden' }}>
                        <div style={{ display: 'flex', gap: 12, alignItems: 'center', background: '#f5f5f5', padding: '6px 12px', flexWrap: 'wrap' }}>
                          <span style={{ color: '#555', fontSize: 11 }}>{fmtDateTime(l.created_at)}</span>
                          <span style={{
                            fontWeight: 600, fontSize: 11, padding: '1px 7px', borderRadius: 10,
                            background: l.direction === 'outbound' ? '#e3f2fd' : '#f3e5f5',
                            color: l.direction === 'outbound' ? '#1565c0' : '#6a1b9a'
                          }}>{l.direction}</span>
                          <span style={{ fontWeight: 600, fontSize: 12 }}>{l.method}</span>
                          <code style={{ fontSize: 12 }}>{l.endpoint}</code>
                          <span style={{
                            marginLeft: 'auto', fontWeight: 700, fontSize: 12,
                            color: l.response_status >= 200 && l.response_status < 300 ? '#2e7d32' : '#c62828'
                          }}>{l.response_status || (l.error_message ? 'ERR' : '-')}</span>
                          <span style={{ color: '#888', fontSize: 11 }}>{l.response_time_ms != null ? `${l.response_time_ms}ms` : ''}</span>
                        </div>
                        {l.error_message && (
                          <div style={{ background: '#fff3e0', color: '#e65100', padding: '6px 12px', fontSize: 12 }}>
                            ⚠️ {l.error_message}
                          </div>
                        )}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
                          <div style={{ borderRight: '1px solid #e0e0e0' }}>
                            <div style={{ padding: '4px 12px', fontSize: 11, fontWeight: 600, color: '#555', background: '#fafafa', borderBottom: '1px solid #e0e0e0' }}>📤 Request Body</div>
                            <pre style={{ margin: 0, padding: '8px 12px', fontSize: 11, overflow: 'auto', maxHeight: 200, background: '#fff' }}>
                              {l.request_body ? JSON.stringify(l.request_body, null, 2) : '(empty)'}
                            </pre>
                          </div>
                          <div>
                            <div style={{ padding: '4px 12px', fontSize: 11, fontWeight: 600, color: '#555', background: '#fafafa', borderBottom: '1px solid #e0e0e0' }}>📥 Response Body</div>
                            <pre style={{ margin: 0, padding: '8px 12px', fontSize: 11, overflow: 'auto', maxHeight: 200, background: '#fff' }}>
                              {l.response_body ? JSON.stringify(l.response_body, null, 2) : '(empty)'}
                            </pre>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {detailTab === 'update-policy' && (
                  <div>
                    {!tabPolicy && <div style={{ color: '#888' }}>Loading…</div>}
                    {tabPolicy?.loading && <div style={{ padding: 20 }}>Loading…</div>}

                    {tabPolicy && !tabPolicy.loading && (
                      <>
                        {tabPolicy.alreadySent && (
                          <div style={{ background: '#fff8e1', padding: 10, borderRadius: 4, marginBottom: 12, fontSize: 13 }}>
                            Already sent <b>{tabPolicy.lastResult}</b> at {fmtDateTime(tabPolicy.lastSentAt)}.
                            Submitting again will overwrite the policy details on APFS.
                          </div>
                        )}

                        {(tabPolicy.warnings || []).map((w) => (
                          <div key={w.code} style={{ background: '#fff3e0', color: '#e65100', padding: 8, borderRadius: 4, marginBottom: 8, fontSize: 13 }}>
                            ⚠️ {w.message}
                          </div>
                        ))}

                        {tabPolicy.error && (
                          <div style={{ color: '#b71c1c', padding: 12, background: '#ffebee', borderRadius: 4, marginBottom: 12, border: '1px solid #ef9a9a' }}>
                            <div style={{ fontWeight: 600, marginBottom: 6 }}>⚠️ {tabPolicy.apfsTitle || 'Error'}</div>
                            {tabPolicy.apfsErrors ? (
                              <ul style={{ margin: '6px 0 0 18px', padding: 0, fontSize: 13 }}>
                                {Object.entries(tabPolicy.apfsErrors).map(([field, msgs]) => (
                                  <li key={field} style={{ marginBottom: 4 }}>
                                    <code style={{ background: '#fff', padding: '1px 4px', borderRadius: 3 }}>{field}</code>
                                    {' — '}
                                    {(Array.isArray(msgs) ? msgs : [String(msgs)]).join('; ')}
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <div style={{ fontSize: 13 }}>{tabPolicy.error}</div>
                            )}
                          </div>
                        )}

                        {tabPolicy.form && (
                          <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 10 }}>
                            <label style={{ alignSelf: 'center' }}><b>Decision</b></label>
                            <div>
                              {['APPROVED', 'REJECTED'].map((d) => (
                                <label key={d} style={{ marginRight: 16, fontSize: 14 }}>
                                  <input type="radio" name="tab-decision" value={d}
                                         checked={tabPolicy.form.decision === d}
                                         onChange={(e) => updateTabPolicyField('decision', e.target.value)} />
                                  {' '}{d}
                                </label>
                              ))}
                            </div>

                            <label style={{ alignSelf: 'center' }}><b>Insurer Code</b></label>
                            <input value={tabPolicy.form.insurerCode || ''}
                                   onChange={(e) => updateTabPolicyField('insurerCode', e.target.value.toUpperCase())}
                                   placeholder="TIP, TNI, …" />

                            <label style={{ alignSelf: 'center' }}><b>Policy Type</b></label>
                            <select value={tabPolicy.form.policyType || 'MOTOR_INSURANCE'}
                                    onChange={(e) => updateTabPolicyField('policyType', e.target.value)}>
                              {POLICY_TYPE_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                            </select>

                            <label style={{ alignSelf: 'center' }}>
                              <b>{tabPolicy.form.decision === 'REJECTED' ? 'Endorsement No.' : 'Policy No.'}</b>
                            </label>
                            <input value={tabPolicy.form.policyNumber || ''}
                                   onChange={(e) => updateTabPolicyField('policyNumber', e.target.value)}
                                   placeholder={tabPolicy.form.decision === 'REJECTED' ? 'Endorsement number' : 'e.g. 69102/รย/010609'} />

                            <label style={{ alignSelf: 'center' }}><b>Policy URL</b></label>
                            <input value={tabPolicy.form.policyUrl || ''}
                                   onChange={(e) => updateTabPolicyField('policyUrl', e.target.value)}
                                   placeholder="optional" />

                            <label style={{ alignSelf: 'center' }}><b>Start Date</b></label>
                            <input type="date" value={tabPolicy.form.policyStartDate || ''}
                                   onChange={(e) => updateTabPolicyField('policyStartDate', e.target.value)} />

                            <label style={{ alignSelf: 'center' }}><b>End Date</b></label>
                            <input type="date" value={tabPolicy.form.policyEndDate || ''}
                                   onChange={(e) => updateTabPolicyField('policyEndDate', e.target.value)} />
                          </div>
                        )}

                        {tabPolicy.result?.success && (
                          <div style={{ background: '#e8f5e9', color: '#1b5e20', padding: 10, borderRadius: 4, marginTop: 16 }}>
                            ✓ Sent to APFS successfully.
                          </div>
                        )}
                        {tabPolicy.result && <PoCheckNotice poCheck={tabPolicy.result.poCheck} />}

                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
                          <button className="btn-search"
                                  onClick={submitTabPolicy}
                                  disabled={tabPolicy.submitting}>
                            {tabPolicy.submitting ? 'Sending…' : (tabPolicy.alreadySent ? 'Update Policy' : 'Send Policy')}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* L.11 LMS status modal */}
      {lmsPropId && (
        <div onClick={() => setLmsPropId(null)}
             style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex',
                      alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
          <div onClick={(e) => e.stopPropagation()}
               style={{ background: '#fff', width: 'min(960px, 95vw)', maxHeight: '92vh', overflow: 'auto',
                        borderRadius: 8, padding: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>L.11 LMS Status — {lmsPropId}</h3>
              <button onClick={() => setLmsPropId(null)}>✕</button>
            </div>
            <LMSStatus key={lmsPropId} initialPropId={lmsPropId} embedded />
          </div>
        </div>
      )}

      {/* Onboarding (L.7) modal */}
      {onboarding && (
        <div onClick={closeOnboarding}
             style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex',
                      alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
          <div onClick={(e) => e.stopPropagation()}
               style={{ background: '#fff', width: 'min(680px, 95vw)', maxHeight: '92vh', overflow: 'auto',
                        borderRadius: 8, padding: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0 }}>
                {onboarding.alreadySent ? 'Update Policy (L.7)' : 'Send Policy (L.7)'} — {onboarding.propId}
              </h3>
              <button onClick={closeOnboarding}>✕</button>
            </div>

            {onboarding.loading && <div style={{ padding: 20 }}>Loading…</div>}

            {onboarding.error && !onboarding.loading && (
              <div style={{ color: '#b71c1c', padding: 12, background: '#ffebee', borderRadius: 4, marginTop: 12, border: '1px solid #ef9a9a' }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>
                  ⚠️ {onboarding.apfsTitle || 'APFS rejected the request'}
                </div>
                {onboarding.apfsErrors ? (
                  <ul style={{ margin: '6px 0 0 18px', padding: 0, fontSize: 13 }}>
                    {Object.entries(onboarding.apfsErrors).map(([field, msgs]) => (
                      <li key={field} style={{ marginBottom: 4 }}>
                        <code style={{ background: '#fff', padding: '1px 4px', borderRadius: 3 }}>{field}</code>
                        {' — '}
                        {(Array.isArray(msgs) ? msgs : [String(msgs)]).join('; ')}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div style={{ fontSize: 13 }}>{onboarding.error}</div>
                )}
                {onboarding.apfsErrors && Object.keys(onboarding.apfsErrors).some((k) => k.includes('insurerCode')) && (
                  <div style={{ marginTop: 8, fontSize: 12, color: '#6d4c41' }}>
                    Tip: APFS UAT only accepts insurer codes from their <code>BrokerInsurerCode</code> enum.
                    Examples confirmed by APFS doc: <b>TIP</b>, <b>TNI</b>. If your insurer isn't on the list,
                    APFS support needs to add it to their enum before L.7 will accept it.
                  </div>
                )}
              </div>
            )}

            {onboarding.alreadySent && !onboarding.loading && (
              <div style={{ background: '#fff8e1', padding: 10, borderRadius: 4, margin: '12px 0', fontSize: 13 }}>
                Already sent <b>{onboarding.lastResult}</b> at {fmtDateTime(onboarding.lastSentAt)}.
                Submitting again will overwrite the policy details on APFS.
              </div>
            )}

            {(onboarding.warnings || []).map((w) => (
              <div key={w.code} style={{ background: '#fff3e0', color: '#e65100', padding: 8, borderRadius: 4, marginTop: 8, fontSize: 13 }}>
                ⚠️ {w.message}
              </div>
            ))}

            {onboarding.form && !onboarding.loading && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 10, marginTop: 16 }}>
                  <label style={{ alignSelf: 'center' }}><b>Decision</b></label>
                  <div>
                    {['APPROVED', 'REJECTED'].map((d) => (
                      <label key={d} style={{ marginRight: 16, fontSize: 14 }}>
                        <input type="radio" name="decision" value={d}
                               checked={onboarding.form.decision === d}
                               onChange={(e) => updateOnboardingField('decision', e.target.value)} />
                        {' '}{d}
                      </label>
                    ))}
                  </div>

                  <label style={{ alignSelf: 'center' }}><b>Insurer Code</b></label>
                  <input value={onboarding.form.insurerCode || ''}
                         onChange={(e) => updateOnboardingField('insurerCode', e.target.value.toUpperCase())}
                         placeholder="TIP, TNI, …" />

                  <label style={{ alignSelf: 'center' }}><b>Policy Type</b></label>
                  <select value={onboarding.form.policyType || 'MOTOR_INSURANCE'}
                          onChange={(e) => updateOnboardingField('policyType', e.target.value)}>
                    {POLICY_TYPE_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>

                  <label style={{ alignSelf: 'center' }}>
                    <b>{onboarding.form.decision === 'REJECTED' ? 'Endorsement No.' : 'Policy No.'}</b>
                  </label>
                  <input value={onboarding.form.policyNumber || ''}
                         onChange={(e) => updateOnboardingField('policyNumber', e.target.value)}
                         placeholder={onboarding.form.decision === 'REJECTED'
                           ? 'Endorsement number'
                           : 'e.g. 69102/รย/010609'} />

                  <label style={{ alignSelf: 'center' }}><b>Policy URL</b></label>
                  <input value={onboarding.form.policyUrl || ''}
                         onChange={(e) => updateOnboardingField('policyUrl', e.target.value)}
                         placeholder="optional" />

                  <label style={{ alignSelf: 'center' }}><b>Start Date</b></label>
                  <input type="date" value={onboarding.form.policyStartDate || ''}
                         onChange={(e) => updateOnboardingField('policyStartDate', e.target.value)} />

                  <label style={{ alignSelf: 'center' }}><b>End Date</b></label>
                  <input type="date" value={onboarding.form.policyEndDate || ''}
                         onChange={(e) => updateOnboardingField('policyEndDate', e.target.value)} />
                </div>

                {onboarding.result && onboarding.result.success && (
                  <div style={{ background: '#e8f5e9', color: '#1b5e20', padding: 10, borderRadius: 4, marginTop: 16 }}>
                    ✓ Sent to APFS successfully.
                  </div>
                )}
                {onboarding.result && <PoCheckNotice poCheck={onboarding.result.poCheck} />}

                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
                  <button onClick={closeOnboarding} disabled={onboarding.submitting}>Close</button>
                  <button className="btn-search"
                          onClick={submitOnboarding}
                          disabled={onboarding.submitting}>
                    {onboarding.submitting
                      ? 'Sending…'
                      : (onboarding.alreadySent ? 'Update Policy' : 'Send Policy')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default APFSSubmitted;
